import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import { TZDate } from 'react-day-picker';

import { Button } from '../Button';
import { Calendar } from '../calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { ScrollArea, ScrollBar } from '../scroll-area';
import { cn } from '../../lib/cn';

export interface DateTimePickerProps {
  label?: string | undefined;
  timeZone?: 'UTC' | undefined;
  value: string;
  onChange: (value: string) => void;
  minDateTime?: string | undefined;
  maxDateTime?: string | undefined;
  minuteStep?: 1 | 5 | undefined;
  className?: string | undefined;
  triggerClassName?: string | undefined;
}

export function DateTimePicker({
  className,
  label = 'Date and time',
  minDateTime,
  maxDateTime,
  minuteStep = 5,
  onChange,
  triggerClassName,
  value,
  timeZone,
}: DateTimePickerProps) {
  const date = parseDateTimeValue(value, timeZone);
  const [isOpen, setIsOpen] = useState(false);
  const minDate = minDateTime ? parseDateTimeValue(minDateTime, timeZone) : undefined;
  const maxDate = maxDateTime ? parseDateTimeValue(maxDateTime, timeZone) : undefined;
  const minutes = Array.from({ length: 60 / minuteStep }, (_, index) => index * minuteStep);

  function handleDateSelect(selectedDate: Date | undefined) {
    if (!selectedDate) return;
    const nextDate = copyDate(selectedDate);
    nextDate.setHours(date?.getHours() ?? 0, date?.getMinutes() ?? 0, 0, 0);
    handleDateChange(nextDate, true);
  }

  function handleTimeChange(type: DateTimePart, nextValue: string) {
    if (!date) return;
    const nextDate = copyDate(date);
    if (type === DateTimePart.Hour) {
      nextDate.setHours(Number.parseInt(nextValue, 10));
    } else {
      nextDate.setMinutes(Number.parseInt(nextValue, 10));
    }
    handleDateChange(nextDate, type === DateTimePart.Hour);
  }

  function handleDateChange(nextDate: Date, clampToBounds = false) {
    if (clampToBounds) {
      nextDate.setTime(
        Math.min(
          maxDate?.getTime() ?? Infinity,
          Math.max(minDate?.getTime() ?? -Infinity, nextDate.getTime()),
        ),
      );
    }
    if (minDate && nextDate.getTime() < minDate.getTime()) return;
    if (maxDate && nextDate.getTime() > maxDate.getTime()) return;
    onChange(formatDateTimeValue(nextDate));
  }

  return (
    <div className={cn('flex min-w-0', className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            className={cn(
              'border-input bg-card text-foreground hover:border-ring focus-visible:ring-ring/50',
              'h-10 w-full max-w-[380px] justify-start rounded-md px-3 text-left text-sm font-normal shadow-[var(--shadow-control)] focus-visible:ring-[3px]',
              !date && 'text-muted-foreground',
              triggerClassName,
            )}
            aria-label={`${label} ${date ? formatDateTimeLabel(date) : 'Select date and time'}`}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 truncate">
              {date ? formatDateTimeLabel(date) : 'MM/DD/YYYY HH:mm'}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          aria-label={`${label} picker`}
          className="w-auto overflow-hidden p-0"
        >
          <div className="sm:flex">
            <Calendar
              mode="single"
              timeZone={timeZone}
              className="bg-popover w-[280px] p-3"
              classNames={{
                today: 'bg-transparent text-foreground rounded-md',
                day: 'group/day relative flex h-8 w-8 items-center justify-center p-0 text-center',
                day_button:
                  'border-border/70 text-foreground hover:bg-accent hover:text-accent-foreground h-8 w-8 min-w-8 rounded-md border text-sm transition-colors data-[selected-single=true]:border-primary data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[selected-single=true]:hover:bg-primary',
              }}
              selected={date}
              showOutsideDays={false}
              {...(date ? { defaultMonth: date } : {})}
              disabled={[
                ...(minDate ? [{ before: startOfDay(minDate) }] : []),
                ...(maxDate ? [{ after: startOfDay(maxDate) }] : []),
              ]}
              onSelect={handleDateSelect}
            />
            <div className="border-border flex flex-col sm:h-[300px] sm:flex-row sm:border-l">
              <TimePartPicker
                ariaLabel="Hour"
                values={hours}
                selectedValue={date?.getHours()}
                isDisabled={(hour) =>
                  isOutsideBounds(date, minDate, maxDate, DateTimePart.Hour, hour)
                }
                onSelect={(hour) => {
                  handleTimeChange(DateTimePart.Hour, hour);
                }}
              />
              <TimePartPicker
                ariaLabel="Minute"
                values={minutes}
                selectedValue={date?.getMinutes()}
                isDisabled={(minute) =>
                  isOutsideBounds(date, minDate, maxDate, DateTimePart.Minute, minute)
                }
                onSelect={(minute) => {
                  handleTimeChange(DateTimePart.Minute, minute);
                }}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function TimePartPicker({
  ariaLabel,
  isDisabled,
  onSelect,
  selectedValue,
  values,
}: {
  ariaLabel: string;
  isDisabled: (value: number) => boolean;
  onSelect: (value: string) => void;
  selectedValue: number | undefined;
  values: number[];
}) {
  return (
    <ScrollArea className="w-64 border-t sm:w-14 sm:border-t-0">
      <div className="flex gap-1 p-2 sm:flex-col">
        {values.map((value) => {
          const label = String(value).padStart(2, '0');

          return (
            <Button
              key={value}
              type="button"
              size="icon"
              variant={selectedValue === value ? 'default' : 'ghost'}
              aria-label={`${ariaLabel} ${label}`}
              disabled={isDisabled(value)}
              className="aspect-square shrink-0 rounded-md text-sm tabular-nums sm:w-full"
              onClick={() => {
                onSelect(String(value));
              }}
            >
              {label}
            </Button>
          );
        })}
      </div>
      <ScrollBar orientation="horizontal" className="sm:hidden" />
    </ScrollArea>
  );
}

enum DateTimePart {
  Hour = 'hour',
  Minute = 'minute',
}

function parseDateTimeValue(value: string, timeZone?: 'UTC') {
  const [dateValue = '', timeValue = ''] = value.split('T');
  const [year, month, day] = dateValue.split('-').map(Number);
  const [hour = 0, minute = 0] = timeValue.split(':').map(Number);
  if (!year || !month || !day) return undefined;
  const date = timeZone
    ? new TZDate(year, month - 1, day, hour, minute, 0, 0, timeZone)
    : new Date(year, month - 1, day, hour, minute, 0, 0);
  if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
    return undefined;
  }
  return date;
}

function formatDateTimeValue(date: Date) {
  return `${formatDateValue(date)}T${formatTimeValue(date)}`;
}

function formatDateTimeLabel(date: Date) {
  return `${formatDisplayDate(date)} ${formatTimeValue(date)}`;
}

function formatDateValue(date: Date) {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatTimeValue(date: Date) {
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${hour}:${minute}`;
}

const displayDateFormats = new Map<string | undefined, Intl.DateTimeFormat>();

function formatDisplayDate(date: Date) {
  const timeZone = date instanceof TZDate ? date.timeZone : undefined;
  let format = displayDateFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      ...(timeZone ? { timeZone } : {}),
    });
    displayDateFormats.set(timeZone, format);
  }
  return format.format(date);
}

function copyDate(date: Date) {
  return date instanceof TZDate ? new TZDate(date.getTime(), date.timeZone) : new Date(date);
}

function startOfDay(date: Date) {
  const start = copyDate(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

function isOutsideBounds(
  date: Date | undefined,
  minDate: Date | undefined,
  maxDate: Date | undefined,
  part: DateTimePart,
  value: number,
) {
  if (!date) return false;
  const earliest = copyDate(date);
  const latest = copyDate(date);
  if (part === DateTimePart.Hour) {
    earliest.setHours(value, 0);
    latest.setHours(value, 59);
  } else {
    earliest.setMinutes(value);
    latest.setMinutes(value);
  }
  return Boolean(
    (minDate && latest.getTime() < minDate.getTime()) ||
    (maxDate && earliest.getTime() > maxDate.getTime()),
  );
}

const hours = Array.from({ length: 24 }, (_, index) => index);
